import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Stack,
    Card,
    Container,
    useTheme,
    CircularProgress
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../LanguageProvider';
import { useAuth } from '../context/AuthContext';
import {
    AutoAwesome,
    CheckCircle,
} from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';

const priceVariants = [
    { label: 'RUB', free: '0₽', smart: '399₽', ultimate: '999₽', advanced: '1999₽', perMonth: '/мес.' },
    { label: 'BYN', free: '0 BYN', smart: '14.99 BYN', ultimate: '39.99 BYN', advanced: '74.99 BYN', perMonth: '/мес.' },
    { label: 'USD', free: '0$', smart: '4.99$', ultimate: '12.99$', advanced: '24.99$', perMonth: '/mo.' },
    { label: 'EUR', free: '0€', smart: '4.49€', ultimate: '11.99€', advanced: '22.99€', perMonth: '/mo.' },
];

const planHierarchy = {
    'Free': 0,
    'Smart': 1,
    'Ultimate': 2,
    'Advanced': 3,
    'Enterprise': 4
};

const AnimatedPrice = ({ planKey }) => {
    const [currencyIdx, setCurrencyIdx] = React.useState(0);
    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrencyIdx(idx => (idx + 1) % priceVariants.length);
        }, 2500);
        return () => clearInterval(interval);
    }, []);
    const current = priceVariants[currencyIdx];
    return (
        <Typography sx={{ fontWeight: 700, fontSize: '2.1rem', mt: -1.2, mb: -1, color: '#7c4dff' }}>
            <AnimatePresence mode="wait" initial={false}>
                <motion.span
                    key={current[planKey] + current.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35 }}
                    style={{ display: 'inline-block' }}
                >
                    {current[planKey]}
                </motion.span>
            </AnimatePresence>
            <span style={{ fontSize: '1rem', color: '#b39ddb', fontWeight: 400, marginLeft: 4 }}>
                {current.perMonth}
            </span>
        </Typography>
    );
};

function Plans() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { currentLanguageData } = useLanguage();
    const { user } = useAuth();
    const [isLoading, setLoading] = useState("none");

    const isRussianDomain = window.location.hostname === 'chat.acceptancegroup.org' || window.location.hostname === 'localhost';

    const getUserCurrentPlan = () => {
        if (!user || !user.ai || !user.ai.smart) return 'Free';
        return user.ai.smart.type || 'Free';
    };

    const currentPlan = getUserCurrentPlan();
    const currentPlanLevel = planHierarchy[currentPlan] || 0;

    const getPlanStatus = (planKey) => {
        const planName = planKey === 'free' ? 'Free' :
            planKey === 'smart' ? 'Smart' :
                planKey === 'ultimate' ? 'Ultimate' :
                    planKey === 'advanced' ? 'Advanced' : 'Free';

        const planLevel = planHierarchy[planName];

        if (planLevel === currentPlanLevel) {
            return { isCurrent: true, isDisabled: false };
        } else if (planLevel < currentPlanLevel) {
            return { isCurrent: false, isDisabled: true };
        } else {
            return { isCurrent: false, isDisabled: false };
        }
    };

    const handleSubscriptionProcess = async (subscriptionName) => {
        try {
            setLoading(subscriptionName);

            const token = user?.jwt_token;

            const url = `https://api.screwltd.com/v3/cloud/payments/subscription/process?subscription=${subscriptionName}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.formUrl) {
                window.location.href = data.formUrl;
            }
        } catch (error) {
            console.error('Error processing subscription:', error);
        } finally {
            setLoading("none");
        }
    };

    return (
        <Box sx={{ minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{
                pt: 14,
                pb: -2,
            }}>
                <Container maxWidth="lg">
                    <Stack spacing={1} sx={{ textAlign: 'center', alignItems: 'center' }}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography
                                level="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                    background: theme.palette.mode === 'dark' ? '#7c4dff' : '#8e24aa',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    color: 'transparent'
                                }}
                            >
                                {currentLanguageData.pricingTitle || 'Pricing Plans'}
                            </Typography>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.2rem' },
                                    color: 'text.secondary',
                                    maxWidth: '600px',
                                    mx: 'auto'
                                }}
                            >
                                {currentLanguageData.pricingSubtitle || 'Choose the optimal plan for you and your business'}
                            </Typography>
                        </motion.div>
                    </Stack>
                </Container>
            </Box>

            {/* Plans Section - Using exact UI from Home.js */}
            <Box sx={{
                pb: 6,
                py: 2, // Добавляем отступы для glow эффекта
            }}>
                <Stack spacing={2} sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 1, sm: 3 }, alignItems: 'center' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: { xs: 2, sm: 4 },
                            userSelect: 'none',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            maxWidth: '100vw',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            p: { xs: 0, xl: 2 }, // Добавляем отступы для glow эффекта
                        }}
                    >
                        {/* Free Plan */}
                        <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex', ml: { xs: 2, sm: 0 }, py: 2 }}>
                            <Card variant="outlined" sx={{
                                borderRadius: 20,
                                minHeight: 440,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(120deg, #221a33cc 60%, #2a1840cc 100%)'
                                    : 'linear-gradient(120deg, #f3e6faee 60%, #ede7f6ee 100%)',
                                border: '2.5px solid',
                                borderColor: theme.palette.mode === 'dark' ? 'rgba(179,136,255,0.18)' : '#e1bee7',
                                p: 3.5,
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 4px 32px 0 rgba(142,36,170,0.22), 0 1.5px 8px 0 rgba(124,77,255,0.10)'
                                    : '0 4px 24px 0 rgba(160,132,202,0.13)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                position: 'relative',
                                transition: 'box-shadow 0.3s, border-color 0.3s, background 0.3s',
                            }}>
                                <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>
                                    {currentLanguageData.planFree || 'Capricorn Free'}
                                </Typography>
                                <AnimatedPrice planKey="free" />
                                <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>
                                    {currentLanguageData.freeDesc || 'Basic free access'}
                                </Typography>
                                <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                    {(currentLanguageData.freeFeatures || [
                                        'Access to basic models',
                                        '10 requests per day',
                                        'Up to 300 characters per message',
                                        'Model updates',
                                        'Basic support',
                                        'Community access',
                                    ]).map((f, i) => (
                                        <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                            <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                            <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                                {(() => {
                                    const { isCurrent, isDisabled } = getPlanStatus('free');
                                    return (
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            fullWidth
                                            disabled={isDisabled}
                                            sx={{
                                                borderRadius: 99,
                                                fontWeight: 600,
                                                fontSize: '0.98rem',
                                                background: isCurrent
                                                    ? '#b266ff'
                                                    : (theme.palette.mode === 'dark' ? '#7c4dff' : '#8e24aa'),
                                                color: '#fff',
                                                boxShadow: 'none',
                                                py: 1.5,
                                                mt: 'auto',
                                                transition: 'background 0.2s',
                                                opacity: isDisabled ? 0.5 : 1,
                                                cursor: isCurrent ? 'default' : 'pointer',
                                                '&:hover': {
                                                    background: isCurrent
                                                        ? '#b266ff'
                                                        : (theme.palette.mode === 'dark' ? '#7048db' : '#701c87'),
                                                },
                                                '&:disabled': {
                                                    background: 'linear-gradient(90deg, #9e9e9e 40%, #bdbdbd 100%)',
                                                }
                                            }}
                                        >
                                            {isCurrent
                                                ? (currentLanguageData.currentPlan || 'Current Plan')
                                                : (currentLanguageData.getStarted || 'Get Started')
                                            }
                                        </Button>
                                    );
                                })()}
                            </Card>
                        </Box>

                        {/* Smart Plan */}
                        <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex', py: 2 }}>
                            <Card variant="outlined" sx={{
                                borderRadius: 20,
                                minHeight: 440,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(120deg, #221a33cc 60%, #2a1840cc 100%)'
                                    : 'linear-gradient(120deg, #f3e6faee 60%, #ede7f6ee 100%)',
                                border: '2.5px solid',
                                borderColor: theme.palette.mode === 'dark' ? 'rgba(179,136,255,0.18)' : '#e1bee7',
                                p: 3.5,
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 4px 32px 0 rgba(142,36,170,0.22), 0 1.5px 8px 0 rgba(124,77,255,0.10)'
                                    : '0 4px 24px 0 rgba(160,132,202,0.13)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                position: 'relative',
                                transition: 'box-shadow 0.3s, border-color 0.3s, background 0.3s',
                            }}>
                                <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>
                                    {currentLanguageData.planSmart || 'Capricorn Smart'}
                                </Typography>
                                <AnimatedPrice planKey="smart" />
                                <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>
                                    {currentLanguageData.smartDesc || 'Creative access'}
                                </Typography>
                                <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                    {(currentLanguageData.smartFeatures || [
                                        'All Free features',
                                        '50 requests per day',
                                        'Up to 500 characters per message',
                                        'Image generation',
                                        'Audio generation',
                                        'Runtime utilities',
                                    ]).map((f, i) => (
                                        <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                            <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                            <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                                {(() => {
                                    const { isCurrent, isDisabled } = getPlanStatus('smart');
                                    return (
                                        <Button
                                            onClick={isCurrent ? undefined : (() => { if (!isDisabled) handleSubscriptionProcess('Smart'); })}
                                            variant="solid"
                                            loading={isLoading == 'Smart'}
                                            color="primary"
                                            fullWidth
                                            disabled={isDisabled}
                                            sx={{
                                                borderRadius: 99,
                                                fontWeight: 600,
                                                fontSize: '0.98rem',
                                                background: isCurrent
                                                    ? '#b266ff'
                                                    : (theme.palette.mode === 'dark' ? '#7c4dff' : '#8e24aa'),
                                                color: '#fff',
                                                boxShadow: 'none',
                                                py: 1.5,
                                                mt: 'auto',
                                                transition: 'background 0.2s',
                                                opacity: isDisabled ? 0.5 : 1,
                                                cursor: isCurrent ? 'default' : 'pointer',
                                                '&:hover': {
                                                    background: isCurrent
                                                        ? '#b266ff'
                                                        : (theme.palette.mode === 'dark' ? '#7048db' : '#701c87'),
                                                },
                                                '&:disabled': {
                                                    background: isLoading != 'Smart' && ('linear-gradient(90deg, #9e9e9e 40%, #bdbdbd 100%)'),
                                                },
                                                '.MuiButton-loadingIndicatorCenter .MuiCircularProgress-svg': {
                                                    '--CircularProgress-progressColor': (theme.palette.mode === 'dark' ? '#7048db' : '#701c87'),
                                                    '--CircularProgress-trackColor': 'transparent'
                                                }
                                            }}
                                        >
                                            {isCurrent
                                                ? (currentLanguageData.currentPlan || 'Current Plan')
                                                : (currentLanguageData.getStarted || 'Get Started')
                                            }
                                        </Button>
                                    );
                                })()}
                            </Card>
                        </Box>

                        {/* Ultimate Plan */}
                        <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex', py: 2 }}>
                            <Card variant="outlined" sx={{
                                borderRadius: 20,
                                minHeight: 440,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(120deg, #221a33cc 60%, #2a1840cc 100%)'
                                    : 'linear-gradient(120deg, #f3e6faee 60%, #ede7f6ee 100%)',
                                border: '3px solid',
                                borderColor: theme.palette.mode === 'dark' ? '#7c4dff' : '#8e24aa',
                                p: 3.5,
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 4px 32px 0 rgba(142,36,170,0.22), 0 1.5px 8px 0 rgba(124,77,255,0.10)'
                                    : '0 4px 24px 0 rgba(160,132,202,0.13)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                position: 'relative',
                                transition: 'box-shadow 0.3s, border-color 0.3s, background 0.3s',
                            }}>
                                <Box sx={{
                                    position: 'absolute',
                                    top: -18,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 3,
                                    px: 2.2, py: 0.7,
                                    borderRadius: 99,
                                    fontWeight: 700,
                                    fontSize: '0.92rem',
                                    color: '#fff',
                                    background: theme.palette.mode === 'dark' ? '#7c4dff' : '#8e24aa',
                                    boxShadow: '0 2px 8px 0 #7c4dff55',
                                    letterSpacing: 0.2,
                                    pointerEvents: 'none',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {currentLanguageData.mostPopularChip || 'Most popular'}
                                </Box>
                                <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>
                                    {currentLanguageData.planUltimate || 'Capricorn Ultimate'}
                                </Typography>
                                <AnimatedPrice planKey="ultimate" />
                                <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>
                                    {currentLanguageData.ultimateDesc || 'Priority access'}
                                </Typography>
                                <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                    {(currentLanguageData.ultimateFeatures || [
                                        'All Smart features',
                                        '200 requests per day',
                                        'Up to 1000 characters per message',
                                        'Priority server mode',
                                        'Dedicated servers under high load',
                                    ]).map((f, i) => (
                                        <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                            <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                            <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                                {(() => {
                                    const { isCurrent, isDisabled } = getPlanStatus('ultimate');
                                    return (
                                        <Button
                                            onClick={isCurrent ? undefined : (() => { if (!isDisabled) handleSubscriptionProcess('Ultimate'); })}
                                            variant="solid"
                                            color="primary"
                                            fullWidth
                                            loading={isLoading == 'Ultimate'}
                                            disabled={isDisabled}
                                            sx={{
                                                borderRadius: 99,
                                                fontWeight: 600,
                                                fontSize: '0.98rem',
                                                background: isCurrent
                                                    ? '#b266ff'
                                                    : (theme.palette.mode === 'dark' ? '#7c4dff' : '#8e24aa'),
                                                color: '#fff',
                                                boxShadow: 'none',
                                                py: 1.5,
                                                mt: 'auto',
                                                transition: 'background 0.2s',
                                                opacity: isDisabled ? 0.5 : 1,
                                                cursor: isCurrent ? 'default' : 'pointer',
                                                '&:hover': {
                                                    background: isCurrent
                                                        ? '#b266ff'
                                                        : (theme.palette.mode === 'dark' ? '#7048db' : '#701c87'),
                                                },
                                                '&:disabled': {
                                                    background: isLoading != 'Ultimate' && ('linear-gradient(90deg, #9e9e9e 40%, #bdbdbd 100%)'),
                                                },
                                                '.MuiButton-loadingIndicatorCenter .MuiCircularProgress-svg': {
                                                    '--CircularProgress-progressColor': (theme.palette.mode === 'dark' ? '#7048db' : '#701c87'),
                                                    '--CircularProgress-trackColor': 'transparent'
                                                }
                                            }}
                                        >
                                            {isCurrent
                                                ? (currentLanguageData.currentPlan || 'Current Plan')
                                                : (currentLanguageData.getStarted || 'Get Started')
                                            }
                                        </Button>
                                    );
                                })()}
                            </Card>
                        </Box>

                        {/* Advanced Plan */}
                        <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex', mr: { xs: 2, sm: 0 }, py: 2 }}>
                            <Card variant="outlined" sx={{
                                borderRadius: 20,
                                minHeight: 440,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(120deg, #221a33cc 60%, #2a1840cc 100%)'
                                    : 'linear-gradient(120deg, #f3e6faee 60%, #ede7f6ee 100%)',
                                border: '2.5px solid',
                                borderColor: theme.palette.mode === 'dark' ? 'rgba(179,136,255,0.18)' : '#e1bee7',
                                p: 3.5,
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 4px 32px 0 rgba(142,36,170,0.22), 0 1.5px 8px 0 rgba(124,77,255,0.10)'
                                    : '0 4px 24px 0 rgba(160,132,202,0.13)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                position: 'relative',
                                transition: 'box-shadow 0.3s, border-color 0.3s, background 0.3s',
                            }}>
                                <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>
                                    {currentLanguageData.planAdvanced || 'Capricorn Advanced'}
                                </Typography>
                                <AnimatedPrice planKey="advanced" />
                                <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>
                                    {currentLanguageData.advancedDesc || 'Full access without limits'}
                                </Typography>
                                <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                    {(currentLanguageData.advancedFeatures || [
                                        'All Ultimate features',
                                        'Unlimited characters',
                                        '1000 requests per day',
                                        'Developer mode',
                                        'Beta model access',
                                        'Extended technical support',
                                    ]).map((f, i) => (
                                        <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                            <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                            <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                                {(() => {
                                    const { isCurrent, isDisabled } = getPlanStatus('advanced');
                                    return (
                                        <Button
                                            onClick={isCurrent ? undefined : (() => { if (!isDisabled) handleSubscriptionProcess('Advanced'); })}
                                            variant="solid"
                                            color="primary"
                                            fullWidth
                                            loading={isLoading == 'Advanced'}
                                            disabled={isDisabled}
                                            sx={{
                                                borderRadius: 99,
                                                fontWeight: 600,
                                                fontSize: '0.98rem',
                                                background: isCurrent
                                                    ? '#b266ff'
                                                    : (theme.palette.mode === 'dark' ? '#7c4dff' : '#8e24aa'),
                                                color: '#fff',
                                                boxShadow: 'none',
                                                py: 1.5,
                                                mt: 'auto',
                                                transition: 'background 0.2s',
                                                opacity: isDisabled ? 0.5 : 1,
                                                cursor: isCurrent ? 'default' : 'pointer',
                                                '&:hover': {
                                                    background: isCurrent
                                                        ? '#b266ff'
                                                        : (theme.palette.mode === 'dark' ? '#7048db' : '#701c87'),
                                                },
                                                '&:disabled': {
                                                    background: isLoading != 'Advanced' && ('linear-gradient(90deg, #9e9e9e 40%, #bdbdbd 100%)'),
                                                },
                                                '.MuiButton-loadingIndicatorCenter .MuiCircularProgress-svg': {
                                                    '--CircularProgress-progressColor': (theme.palette.mode === 'dark' ? '#7048db' : '#701c87'),
                                                    '--CircularProgress-trackColor': 'transparent'
                                                }
                                            }}
                                        >
                                            {isCurrent
                                                ? (currentLanguageData.currentPlan || 'Current Plan')
                                                : (currentLanguageData.getStarted || 'Get Started')
                                            }
                                        </Button>
                                    );
                                })()}
                            </Card>
                        </Box>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
}

export default Plans;
